import { Card, Typography, CardActions, IconButton, CardContent, Button } from "@mui/material";
import { MealItem, MealItemSnapshot, MealSnapshot, NutritionCount } from "../../../../service/model/nutrition.model";
import { AddIcon, DeleteIcon } from "../../../../shared/icons";
import { useEffect, useState } from "react";
import MealItemRow from "./meal-item-row";
import { Unit } from "../../../../service/model/unit.model";
import useUnits from "../../../../hooks/use-units";
import theme from "../../../../app/app-theme";
import { getNutritionCount } from "../../../../shared/utils";

export type MealItemWithDetails = {
    id?: number;
    name?: string;
    quantity?: number;
    unit?: Unit;
    nutrition?: NutritionCount;
};

interface MealCardProps {
    meal: MealSnapshot;
    mealItems: MealItem[];
    onDelete: () => void;
    onMealItemsChange?: (index: number, mealItems: MealItemSnapshot[]) => void;
}

const MealCard: React.FC<MealCardProps> = ({ meal, mealItems, onDelete, onMealItemsChange }) => {
    const [units] = useUnits();
    const [selectedMealItems, setSelectedMealItems] = useState<MealItemWithDetails[]>(meal.items.map(item => ({
        id: item.mealItemId,
        name: item.name,
        quantity: item.quantity,
        unit: units.find(u => u.id === item.unitId),
        nutrition: {
            calories: item.nutrition?.calories,
            protein: item.nutrition?.protein,
            carbohydrates: item.nutrition?.carbohydrates,
            fat: item.nutrition?.fat,
            fiber: item.nutrition?.fiber,
            sugar: item.nutrition?.sugar,
        }
    })) ?? []);

    const handleAddMealItem = () => {
        const newItem: MealItemWithDetails = {
            id: undefined,
            name: undefined,
            quantity: undefined,
            unit: undefined,
        };
        setSelectedMealItems((prevItems) => [...prevItems, newItem]);
    };

    useEffect(() => {
        const mealItemSnapshots: MealItemSnapshot[] = selectedMealItems.map((item) => ({
            mealItemId: item.id ?? 0,
            name: mealItems.find((mi) => mi.id === item.id)?.name ?? "",
            quantity: item.quantity ?? 0,
            unitId: item.unit?.id ?? 0,
            unitSymbol: item.unit?.symbol ?? "",
            nutrition: {
                calories: getNutritionCount(mealItems.find(mi => mi.id === item.id)?.defaultQuantity, item.quantity, mealItems.find(mi => mi.id === item.id)?.nutrition?.calories),
                protein: getNutritionCount(mealItems.find(mi => mi.id === item.id)?.defaultQuantity, item.quantity, mealItems.find(mi => mi.id === item.id)?.nutrition?.protein),
                carbohydrates: getNutritionCount(mealItems.find(mi => mi.id === item.id)?.defaultQuantity, item.quantity, mealItems.find(mi => mi.id === item.id)?.nutrition?.carbohydrates),
                fat: getNutritionCount(mealItems.find(mi => mi.id === item.id)?.defaultQuantity, item.quantity, mealItems.find(mi => mi.id === item.id)?.nutrition?.fat),
                fiber: getNutritionCount(mealItems.find(mi => mi.id === item.id)?.defaultQuantity, item.quantity, mealItems.find(mi => mi.id === item.id)?.nutrition?.fiber),
                sugar: getNutritionCount(mealItems.find(mi => mi.id === item.id)?.defaultQuantity, item.quantity, mealItems.find(mi => mi.id === item.id)?.nutrition?.sugar),
            }
        })).filter((snapshot) => snapshot.mealItemId !== 0); // Filter out items without a valid mealItemId
        onMealItemsChange?.(meal.index, mealItemSnapshots);
    }, [selectedMealItems]);

    return (
        <Card sx={{ p: 2, mb: 2, height: "100%", display: "flex", flexDirection: "column", backgroundColor: theme.palette.info.light }}>
            <CardContent>
                <Typography variant="h6">{meal.mealTime}</Typography>
                {/* <Chip label={`Total Calories (Meal): ${meal.totalMealNutrition?.calories ?? 0}`} variant="filled" color="info" sx={{ mt: 1, mb: 2 }} /> */}
                {selectedMealItems.map((item) => (
                    <MealItemRow
                        key={item.id || Math.random()} // Use mealItem id if available, otherwise generate a random key
                        mealItem={item}
                        mealItems={mealItems.filter((mi) => !selectedMealItems.some((smi) => smi.id === mi.id) || mi.id === item.id)} // Exclude already selected meal items except the current one
                        onQuantityChange={(quantity) => {
                            setSelectedMealItems((prevItems) =>
                                prevItems.map((i) =>
                                    i.id === item.id ? { ...i, quantity } : i
                                )
                            );
                        }}
                        onMealItemChange={(mealItem) => {
                            setSelectedMealItems((prevItems) =>
                                prevItems.map((i) =>
                                    i.id === item.id ? { ...i, ...mealItem, name: mealItem.name, unit: mealItem.defaultUnit, quantity: mealItem.defaultQuantity } : i
                                )
                            );
                        }}
                        onUnitChange={(unit) => {
                            setSelectedMealItems((prevItems) =>
                                prevItems.map((i) =>
                                    i.id === item.id ? { ...i, unit } : i
                                )
                            );
                        }}
                        onDelete={() => {
                            setSelectedMealItems((prevItems) =>
                                prevItems.filter((i) => i.id !== item.id)
                            );
                        }}
                    />
                ))}
                {/* <Grid container justifyContent="center">
                    <Chip label={`Total Calories (Meal): ${meal.totalMealNutrition?.calories ?? 0}`} variant="filled" color="info" />
                </Grid> */}
                <Button
                    variant="outlined"
                    sx={{ mt: 3, mb: 2, justifyContent: 'center' }}
                    onClick={handleAddMealItem}
                >
                    <AddIcon sx={{ mr: 1 }} /> Add meal item
                </Button>
            </CardContent>

            <CardActions disableSpacing sx={{ mt: 'auto' }}>
                <IconButton aria-label="delete meal" title="Delete meal" color="error">
                    <DeleteIcon onClick={onDelete} />
                </IconButton>
            </CardActions>
        </Card>
    );
};

export default MealCard;
